import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import webrtc from '@/services/webrtc';

// Axios interceptor to add the active extension to every request
axios.interceptors.request.use((config) => {
  const activeExten = localStorage.getItem('active_exten');
  if (activeExten) {
    config.headers['X-Active-Exten'] = activeExten;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on initial load
    const token = localStorage.getItem('session_token');
    if (token) {
      validateSession(token);
    } else {
      setLoading(false);
    }
  }, []);

  const validateSession = async (token) => {
    try {
      const response = await axios.get(`${API}/validate-session`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.valid && response.data.user) {
        const validatedUser = response.data.user;
        setUser({
          username: validatedUser.username,
          exten: validatedUser.exten,
          id: validatedUser.id
        });
        localStorage.setItem('active_exten', validatedUser.exten);
      } else {
        localStorage.removeItem('session_token');
        localStorage.removeItem('active_exten');
      }
    } catch (error) {
      console.error('Session validation failed:', error);
      localStorage.removeItem('session_token');
      localStorage.removeItem('active_exten');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/login`, { username, password });
      
      if (response.data.token) {
        const { token, user: returnedUser } = response.data;
        localStorage.setItem('session_token', token);
        localStorage.setItem('active_exten', returnedUser.exten);
        setUser({
          username: returnedUser.username,
          exten: returnedUser.exten,
          id: returnedUser.id
        });
        return { success: true };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Falha no login. Verifique suas credenciais.' 
      };
    }
  };

  const logout = () => {
    const token = localStorage.getItem('session_token');
    localStorage.removeItem('session_token');
    localStorage.removeItem('active_exten');
    setUser(null);
    webrtc.disconnect();
    // Best-effort call to backend to invalidate the session
    if (token) {
      axios.post(`${API}/logout`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).catch(console.error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
